import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import type {
  SkillDataTableItem,
  SkillListStateFilter,
} from '@kirin/api-client/contracts/skills';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useSkillListActions } from '@/hooks/api/useSkillListActions';
import {
  skillGroupRuleItem,
  useInventoryGroupRuleActions,
  type SkillGroupItem,
} from '@/hooks/api/useInventoryGroupRuleActions';
import { useInventoryScope } from '@/hooks/useInventoryScope';
import { useToast } from '@/hooks/use-toast';
import { GroupScopeResetButton } from '@/components/shared/GroupScopeResetButton';
import { skillsGroupScopeReset } from '@/hooks/api/useGroupScopeReset';
import { PolicyScopeSelector } from '../../components/PolicyScopeSelector';
import { InventoryScopeUnavailableNotice } from '../../components/InventoryScopeUnavailableNotice';
import { createSkillsColumns, type SkillGroupColumnHandlers } from './columns';
import { fetchSkills, skillsQueryKey, type SkillExtraFilters } from './api';
import { SKILLS_TABLE_CONFIG } from './constants';
import { SkillListStateFilterControl } from './SkillListStateFilterControl';
import { SkillRowExpansion } from './SkillRowExpansion';
import { SkillProjectsModal, SkillUsersModal } from './SkillListModals';

interface DataTableSkillsContentProps {
  organizationId: string;
}

/**
 * The rule identity a row writes, org-wide or for a group: one version of the
 * skill (KIR-5174). `name` is the folded key the matcher stores;
 * `displayName` is only for toasts.
 */
function identityOf(row: SkillDataTableItem): SkillGroupItem & {
  displayName: string;
} {
  return {
    name: row.name,
    source: row.source,
    project: row.project,
    version: row.version,
    displayName: row.displayName,
  };
}

/**
 * Inventory → Skills (KIR-4699).
 *
 * Organization routes only: there is no account-scoped skills route, so the
 * tab is not offered on the personal dashboard. KIR-4701 adds a security
 * group scope, where a choice is Allow/Block for the group's members.
 */
export function DataTableSkillsContent({
  organizationId,
}: DataTableSkillsContentProps): ReactElement {
  const [listState, setListState] = useState<SkillListStateFilter>('all');
  const [markTarget, setMarkTarget] = useState<SkillDataTableItem | null>(null);
  const [markOpen, setMarkOpen] = useState(false);
  // The scope the confirmation opened in (group id, or `null` org-wide).
  // Confirm writes only while the live scope still matches it.
  // The dialog's copy comes from it too, so it never describes a write the
  // guard then refuses.
  const [markScope, setMarkScope] = useState<{
    groupId: string | null;
    groupName: string | null;
  }>({ groupId: null, groupName: null });
  const { toast } = useToast();
  // Row and openness are separate on purpose: the dialog stays mounted while
  // it animates out, so the row has to outlive the close or the title blanks
  // to "(0)" and the empty state flashes on the way out.
  const [projectsTarget, setProjectsTarget] =
    useState<SkillDataTableItem | null>(null);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [usersTarget, setUsersTarget] = useState<SkillDataTableItem | null>(
    null
  );
  const [usersOpen, setUsersOpen] = useState(false);

  const handleProjectsClick = useCallback((row: SkillDataTableItem) => {
    setProjectsTarget(row);
    setProjectsOpen(true);
  }, []);

  const handleUsersClick = useCallback((row: SkillDataTableItem) => {
    setUsersTarget(row);
    setUsersOpen(true);
  }, []);

  // Reading the tab is manager/owner/support, but writing a rule is
  // manager/owner — so a support user must see the toggle disabled rather
  // than click it into a 403.
  const writeGate = useOrgWriteGate(organizationId);
  const canEdit =
    !writeGate.hidden && !writeGate.disabled && !writeGate.isLoading;

  const actions = useSkillListActions(organizationId as OrganizationId);
  const { markForDelete, allow, isPending } = actions;

  // KIR-4701 Allow/Block scope.
  const scope = useInventoryScope(organizationId);
  const securityGroupId = scope.groupId;
  const groupName = scope.groupName;
  const groupActions = useInventoryGroupRuleActions(
    organizationId,
    securityGroupId,
    skillGroupRuleItem
  );

  const handleRequestMark = useCallback(
    (row: SkillDataTableItem) => {
      setMarkTarget(row);
      setMarkScope({ groupId: securityGroupId, groupName });
      setMarkOpen(true);
    },
    [securityGroupId, groupName]
  );

  const extra = useMemo<SkillExtraFilters>(
    () => ({ listState, securityGroupId }),
    [listState, securityGroupId]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchSkills(organizationId, extra, params, signal),
    [organizationId, extra]
  );

  // The pill has to reach the key as well as the request, or switching it
  // would serve the previous pill's rows straight out of cache.
  const getQueryKeys = useCallback(
    (params: DataFetchParams) => skillsQueryKey(organizationId, extra, params),
    [organizationId, extra]
  );

  const handleAllow = useCallback(
    (row: SkillDataTableItem) => {
      void allow(identityOf(row));
    },
    [allow]
  );

  const handleConfirmMark = useCallback(() => {
    if (!markTarget) return;
    if (markScope.groupId !== securityGroupId) {
      // The group was deleted, or the scope changed, while the dialog was
      // open: the write would not land where the dialog said.
      toast({
        title: `${markTarget.displayName} was not blocked`,
        description:
          'The Allow/Block scope changed while the dialog was open. Check the scope and try again.',
        variant: 'destructive',
      });
    } else if (securityGroupId) {
      void groupActions.setChoice(identityOf(markTarget), 'block');
    } else {
      void markForDelete(identityOf(markTarget));
    }
    // Close, but KEEP the row: nulling it here dropped the title back to
    // "Mark this skill for delete?" for the length of the exit animation
    // (KIR-4850 review).
    setMarkOpen(false);
  }, [
    markTarget,
    markScope,
    markForDelete,
    securityGroupId,
    groupActions,
    toast,
  ]);

  const groupHandlers = useMemo<SkillGroupColumnHandlers | undefined>(
    () =>
      securityGroupId && groupName
        ? {
            name: groupName,
            onAllow: row =>
              void groupActions.setChoice(identityOf(row), 'allow'),
            onRequestBlock: handleRequestMark,
            isPending: row => groupActions.isPending(identityOf(row)),
          }
        : undefined,
    [securityGroupId, groupName, groupActions, handleRequestMark]
  );

  const columns = useMemo(
    () =>
      createSkillsColumns({
        onAllow: handleAllow,
        onRequestMark: handleRequestMark,
        isPending: row => isPending(identityOf(row)),
        canEdit,
        // Loading is not "support read-only": the switch stays disabled, but
        // the tooltip would name the wrong reason.
        readOnlyMessage: writeGate.disabled
          ? SUPPORT_READONLY_TOOLTIP
          : undefined,
        onProjectsClick: handleProjectsClick,
        onUsersClick: handleUsersClick,
        organizationId,
        // The filter-options endpoint has no group scope, so the pill's
        // state there would be the organization's, not the group's rows'.
        // Offer every value instead of a misleading subset.
        listState: securityGroupId ? 'all' : listState,
        group: groupHandlers,
      }),
    [
      securityGroupId,
      groupHandlers,
      handleAllow,
      handleRequestMark,
      isPending,
      canEdit,
      writeGate.disabled,
      organizationId,
      listState,
      handleProjectsClick,
      handleUsersClick,
    ]
  );

  const features = useMemo(
    () => ({
      enableSorting: true,
      enableRowExpansion: true,
      enableFiltering: true,
    }),
    []
  );

  const rowExpansion = useMemo(
    () => ({
      // The full identity: two rows can legitimately share a name across
      // sources, repositories or SKILL.md versions (KIR-5174), and expansion
      // state would collide on name.
      getRowId: (row: SkillDataTableItem) =>
        `${row.source}|${row.project ?? ''}|${row.name}|${row.version ? `${row.version.scheme}:${row.version.hash}` : ''}`,
      keepMounted: false,
      render: (row: SkillDataTableItem) => (
        <SkillRowExpansion
          organizationId={organizationId}
          row={row}
          securityGroupId={securityGroupId}
        />
      ),
    }),
    [organizationId, securityGroupId]
  );

  // `listState` is not a column filter, so it is invisible to the count
  // fingerprint unless named here — without it, switching the pill replays
  // the previous pill's total as a knownTotal.
  const countScope = useMemo(
    () =>
      securityGroupId
        ? { organizationId, listState, securityGroupId }
        : { organizationId, listState },
    [organizationId, listState, securityGroupId]
  );

  const queryConfig = useMemo(
    () => ({ staleTime: 30000, refetchOnWindowFocus: false }),
    []
  );

  const search = useMemo(
    () => ({ placeholder: 'Search skills...', className: 'w-[260px]' }),
    []
  );

  const stateFilter = useMemo(
    () => (
      <SkillListStateFilterControl
        value={listState}
        onChange={setListState}
        markedLabel={securityGroupId ? 'Blocked' : undefined}
      />
    ),
    [listState, securityGroupId]
  );

  const handleClearFilters = useCallback(() => setListState('all'), []);

  return (
    <>
      <Alert className="mb-3">
        <Info className="h-4 w-4" />
        <AlertDescription>
          {securityGroupId
            ? `Review the agent skills installed across your organization. Blocking a skill for ${groupName ?? 'this group'} quarantines its files on the endpoints of the group's members — it is an administrative decision, not a security verdict about the skill.`
            : 'Review the agent skills installed across your organization. Marking a skill for delete quarantines its files on every endpoint that has it — it is an administrative decision, not a security verdict about the skill.'}
          {
            ' Risk comes from AgentMesh, matched on the exact files of each skill; expand a skill to see why.'
          }
        </AlertDescription>
      </Alert>

      {scope.selectorVisible && (
        <div className="mb-3 space-y-3 empty:hidden">
          {/* `empty:hidden`: with no groups the selector draws nothing, and
              the page must look exactly as it did before (KIR-4701). */}
          <PolicyScopeSelector
            organizationId={organizationId}
            selectedGroupId={securityGroupId}
            onScopeChange={scope.setScope}
            variant="inventory"
            actions={
              <GroupScopeResetButton
                organizationId={organizationId}
                groupId={securityGroupId}
                groupName={groupName}
                canEdit={canEdit}
                target={skillsGroupScopeReset}
              />
            }
          />
          {scope.requestedScopeUnavailable && (
            <InventoryScopeUnavailableNotice
              onSwitchToOrganization={() => scope.setScope(null)}
            />
          )}
        </div>
      )}

      {/* Until a deep-linked `?scope=` resolves the group is unknown, and an
          edit would land on the organization: hold the table. */}
      {scope.isResolving ? (
        <div data-testid="skills-scope-loading" className="px-4 py-2">
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <DataTable<SkillDataTableItem>
          id={SKILLS_TABLE_CONFIG.tableId}
          mode="server"
          className="flex flex-col flex-1 min-h-0"
          containerClassName="flex-1"
          columns={columns}
          errorBoundaryLabel="Something went wrong in the Skills table"
          search={search}
          features={features}
          rowExpansion={rowExpansion}
          queryFn={queryFn}
          getQueryKeysFromParams={getQueryKeys}
          toolbarEndSlot={stateFilter}
          onClearFilters={handleClearFilters}
          pageResetKey={
            securityGroupId ? `${listState}|${securityGroupId}` : listState
          }
          noDataLabel={
            listState === 'marked_for_delete'
              ? securityGroupId
                ? `No skills are blocked for ${groupName ?? 'this group'}.`
                : 'No skills are marked for delete.'
              : listState === 'allowed'
                ? 'No allowed skills.'
                : 'No skills found.'
          }
          countScope={countScope}
          queryConfig={queryConfig}
        />
      )}

      {markScope.groupId ? (
        <ConfirmationDialog
          isOpen={markOpen}
          onClose={() => setMarkOpen(false)}
          onConfirm={handleConfirmMark}
          title={`Block ${markTarget?.displayName ?? 'this skill'} for ${markScope.groupName ?? 'this group'}?`}
          description={`This applies only to members of ${markScope.groupName ?? 'this group'}. Kirin will quarantine this skill's files on their endpoints the next time each endpoint scans the file. Everyone else keeps the organization's choice.`}
          confirmLabel="Block"
          variant="destructive"
        />
      ) : (
        <ConfirmationDialog
          isOpen={markOpen}
          onClose={() => setMarkOpen(false)}
          onConfirm={handleConfirmMark}
          title={`Mark ${markTarget?.displayName ?? 'this skill'} for delete?`}
          // Two things the copy must not imply, both load-bearing:
          //  - that the skill is already gone. A mark is not retroactive; the
          //    agent acts on the file the next time it scans it.
          //  - that Allow restores a file. Allow deletes the rule, which stops
          //    further quarantining. A file already quarantined stays that way
          //    until someone restores it locally on that endpoint (the agent's
          //    `trust_anyway`) (KIR-4850 review).
          description="Kirin will quarantine this skill's files on every endpoint that has it, taking effect the next time each endpoint scans the file rather than immediately. Allowing later stops further quarantining; files already quarantined stay quarantined until restored locally on each endpoint."
          confirmLabel="Mark for delete"
          variant="destructive"
        />
      )}

      <SkillProjectsModal
        row={projectsTarget}
        open={projectsOpen}
        onClose={() => setProjectsOpen(false)}
      />
      <SkillUsersModal
        row={usersTarget}
        open={usersOpen}
        onClose={() => setUsersOpen(false)}
      />
    </>
  );
}

export default DataTableSkillsContent;
